"use client";

import {
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
  type RefObject,
} from "react";
import type { FileAttachment, ImageAttachment } from "@bash/shared";
import { useAttachmentHandler } from "@/components/message-input";
import {
  referencedRowId,
  type PendingAttachment,
} from "@/components/message-input/AttachmentHandler";
import {
  useComposerFileDrop,
  type ComposerFileDrop,
} from "@/components/message-input/useComposerFileDrop";
import {
  useComposerDraftStore,
  type SharedFileRef,
} from "@/stores/composerDraftStore";

type Lane = ReturnType<typeof useAttachmentHandler>;

/**
 * The send's map from the lane's rows to the two lists a send carries (the
 * pill's own send, and the Line's attach door through useLineAttach): an
 * image rides with its pixels, a file by its door, a row with neither is
 * left out. `named` keeps an image's own filename, so the call can name what
 * it was given; the pill's send leaves it to the server's mint, as it always
 * has.
 */
export function rowsToAttachments(
  rows: PendingAttachment[],
  { named = false }: { named?: boolean } = {}
): { images: ImageAttachment[]; files: FileAttachment[] } {
  const images: ImageAttachment[] = [];
  const files: FileAttachment[] = [];
  for (const a of rows) {
    if (a.isImage && a.imageData)
      images.push({
        data: a.imageData.data,
        mediaType: a.imageData.mediaType,
        ...(named ? { filename: a.filename } : {}),
      });
    else if (a.fileData)
      files.push({
        filename: a.filename,
        url: a.fileData.url,
        mediaType: a.fileData.mediaType,
        sizeBytes: a.fileData.sizeBytes,
      });
  }
  return { images, files };
}

export interface PillFiles {
  pendingAttachments: Lane["pendingAttachments"];
  imageError: Lane["imageError"];
  openFilePicker: Lane["openFilePicker"];
  /** The picker's change handler: remembers the Files, then the upload lane's own. */
  pickFiles: (e: ChangeEvent<HTMLInputElement>) => void;
  resetAttachments: Lane["resetAttachments"];
  removeAttachment: Lane["removeAttachment"];
  isDragOver: boolean;
  dragHandlers: ComposerFileDrop["dragHandlers"];
  onPaste: ComposerFileDrop["onPaste"];
}

/**
 * The pill's files: the shared attach lane, the drop and paste intake, and the
 * parking of unsent Files across the type door. Moved out of BashPill verbatim.
 *
 * The upload lane's rows carry no File, so the pill remembers every File it is
 * handed (the home lane, its own drop or paste, the picker) and, on the door
 * closing with unsent rows, parks the ones still in the strip back into the
 * home lane for the next opening, the way the draft survives. A row and a File
 * are the same file by name and size.
 *
 * Files shared with them ride the same rail (homeRefs, parked by "Use with
 * Bash" in Files, Shared with me), taken once and parked back the same way. A
 * ref is taken on sight, not only on mount: with the Line off the box is the
 * composer on every route and is already on screen when the person clicks (the
 * hand-off navigates home, it never remounts the box), and with the Line on an
 * open door outlives the route change. Only the shell's settle gates it.
 */
export function usePillFiles(
  liveOn: boolean,
  settled: boolean,
  rootRef: RefObject<HTMLElement | null>
): PillFiles {
  const {
    pendingAttachments,
    imageError,
    setImageError,
    openFilePicker,
    handleImageSelect,
    handleFiles,
    addReferencedFiles,
    resetAttachments,
    removeAttachment,
  } = useAttachmentHandler();
  const keptFiles = useRef<File[]>([]);
  const keptRefs = useRef<SharedFileRef[]>([]);
  const intakeFiles = useCallback(
    (files: File[]) => {
      keptFiles.current = [...keptFiles.current, ...files];
      handleFiles(files);
    },
    [handleFiles]
  );
  const pickFiles = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      keptFiles.current = [
        ...keptFiles.current,
        ...Array.from(e.target.files ?? []),
      ];
      handleImageSelect(e);
    },
    [handleImageSelect]
  );
  const { isDragOver, dragHandlers, onPaste } = useComposerFileDrop({
    handleFiles: intakeFiles,
    setImageError,
    rootRef,
  });
  // Files dropped on the ball (the Dock's stage) were parked for this lane,
  // the Dock's pill never rendering there: taken once, on mount.
  useEffect(() => {
    if (!liveOn || !settled) return;
    const files = useComposerDraftStore.getState().takeHomeFiles();
    if (files && files.length > 0) intakeFiles(files);
  }, [liveOn, settled, intakeFiles]);
  const parkedRefs = useComposerDraftStore((s) => s.homeRefs);
  useEffect(() => {
    if (!settled || !parkedRefs) return;
    const refs = useComposerDraftStore.getState().takeHomeRefs();
    if (!refs || refs.length === 0) return;
    keptRefs.current = [...keptRefs.current, ...refs];
    addReferencedFiles(refs);
  }, [parkedRefs, settled, addReferencedFiles]);
  const pendingRef = useRef(pendingAttachments);
  useEffect(() => {
    pendingRef.current = pendingAttachments;
  }, [pendingAttachments]);
  useEffect(() => {
    if (!liveOn) return;
    return () => {
      const rows = pendingRef.current;
      const unsent = keptFiles.current.filter((f) =>
        rows.some((r) => r.filename === f.name && r.sizeBytes === f.size)
      );
      if (unsent.length > 0)
        useComposerDraftStore.getState().parkHomeFiles(unsent);
      const unsentRefs = keptRefs.current.filter((f) =>
        rows.some((r) => r.id === referencedRowId(f.fileId))
      );
      if (unsentRefs.length > 0)
        useComposerDraftStore.getState().parkHomeRefs(unsentRefs);
    };
  }, [liveOn]);
  return {
    pendingAttachments,
    imageError,
    openFilePicker,
    pickFiles,
    resetAttachments,
    removeAttachment,
    isDragOver,
    dragHandlers,
    onPaste,
  };
}
